import './WelcomeScreenView.scss';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../../../components/loader/Loader';
import { isBentley } from '../../../js/config/_global';
import MultiSelectDropdown from '../../../components/multiSelectDropdown/MultiSelectDropdown';
import ImageStatic from '../../../components/imageStatic/ImageStatic';
import InputField from '../../../components/inputField/InputField';
import Button from '../../../components/button/Button';
import Modal from '../../../components/modal/Modal';
import { userStore, useWelcomeScreenStore } from '../../../store';
import DealershipDropdown from '../../../components/dealershipDropdown/DealershipDropdown';
import GoBackButton from '../../../components/goBackButton/GoBackButton';
import helpers from '../../../js/helpers';
import api from '../../../api';
import { RESPONSE, SEARCH_DEBOUNCE_VALUE } from '../../../js/_constants';
import debounce from 'debounce';

interface typeSingleDeviceSelectOptions {
    value: string;
    label: string;
}

interface TemplateList {
    id: number;
    title: string;
    imageUrlOriginal: string,
    imageUrlOptimizedSmall: string,
    imageUrlOptimizedBig: string,
}

// hardcoded urls from DAM
const TEMPLATE_IMAGES_ORIGINAL = [
    // dark image | white text
    'https://delivery-p123922-e1240369.adobeaemcloud.com/adobe/assets/urn:aaid:aem:5d68a093-3f38-4aec-9a59-55e88cbf8463/original/as/A3_2023_5723_Backplate.jpg?accept-experimental',
    // light image | dark text
    'https://delivery-p123922-e1240369.adobeaemcloud.com/adobe/assets/urn:aaid:aem:8c12371d-9aec-49c1-9e0c-b2c47a2e468b/original/as/SQ6_2023_5312_backplate.jpg?accept-experimental',
    // dark image | white text
    'https://delivery-p123922-e1240369.adobeaemcloud.com/adobe/assets/urn:aaid:aem:77efb1b9-73cb-4b4c-92c9-dbf81b69ecf4/original/as/A6e_2023_6005_backplate.jpg?accept-experimental',
    //TODO: add new static images for Bentley, condition only for Bentley
]

const TEMPLATE_IMAGES_ORIGINAL_BENTLEY = [
    "https://delivery-p153381-e1601884.adobeaemcloud.com/adobe/assets/urn:aaid:aem:134c1788-834e-4bdf-9247-e2223b38c293/renditions/original/as/asset_original.jpg",
    "https://delivery-p153381-e1601884.adobeaemcloud.com/adobe/assets/urn:aaid:aem:862a8ef8-ae07-4641-9a4c-de61c191311b/original/as/BNTLY_089_Handover%20Background%20Screen_v2_01-16x9.jpg?accept-experimental"
]

const CUSTOMER_NAME_LENGTH_MIN = 3
const CUSTOMER_NAME_LENGTH_MAX = 30

const DEALER_NAME_LENGTH_MIN = 3
const DEALER_NAME_LENGTH_MAX = 30

const CUSTOMER_MESSAGE_LENGTH_MIN = 3
const CUSTOMER_MESSAGE_LENGTH_MAX = 60

const WelcomeScreenView = ({ contextDealerId, isIframeMode }) => {
    const { t, i18n } = useTranslation();
    const { dealerships } = userStore();
    const { connectWelcomeScreenAVE, dissonnectWelcomeScreenAVE, setWelcomeScreenAVE, setWelcomeScreenChrombox, endWelcomeScreenChrombox} = useWelcomeScreenStore();
    const [loading, setLoading] = useState(false);
    const [devicesList, setDevicesList] = useState<any[]>([]);
    const [deviceSelected, setDeviceSelected] = useState<any | null>(null);
    const [dealershipSelected, setDealershipSelected] = useState(contextDealerId || null);
    const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
    const [imageTemplateList, setImageTemplateList] = useState<TemplateList[]>([]);
    const [customerName, setCustomerName] = useState<string>('');
    const [dealerName, setDealerName] = useState<string>('');
    const [customerMessage, setCustomerMessage] = useState<string>('');
    const [templateImageSelected, setTemplateImageSelected] = useState<string>('');
    const [isDealerNameError, setDealerNameError] = useState(false);
    const [isCustomerNameError, setCustomerNameError] = useState(false);
    const [isCustomerMessageError, setCustomerMessageError] = useState(false);
    const [isFormValidated, setFormValidated] = useState(false);
    const [isFeedbackSuccess, setFeedbackSuccess] =  useState(false);
    const [isFeedbackError, setFeedbackError] =  useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);

    const resetFormData = () => {
        setCustomerName('')
        setDealerName('')
        setCustomerMessage('')
        setTemplateImageSelected('')
        setFormValidated(false)
    }

    const closeModalSuccess = () => {
        setFeedbackSuccess(false)
    }

    const closeModalError = () => {
        setFeedbackError(false)
    }

    const handleSelectChange = (selectedOptions: typeSingleDeviceSelectOptions[]) => {
        // set info about selected device
        if (selectedOptions && selectedOptions.length > 0) {
            const selectedValue = selectedOptions[0].value;
            const selectedDevice = devicesList.find(singleDeviceData => singleDeviceData.deviceNumber === selectedValue);
            if (selectedDevice) {
                setDeviceSelected(selectedDevice);
            }
        }
    };

    const getTemplateImageList = async () => {
        if (deviceSelected) {
            // TODO: this api call now is not useful, images are hardcoded.
            // but in the future they could come from here, so we wait api response
            // await api.assetsOverride.getAssetOverride(deviceSelected.id);

            const templateList: TemplateList[] = []

            const imagesToUse = isBentley ? TEMPLATE_IMAGES_ORIGINAL_BENTLEY : TEMPLATE_IMAGES_ORIGINAL

            imagesToUse.forEach((singleImage: string, index) => {
                const indexTemplate = index + 1
                const singleTemplateItem = {
                    id: indexTemplate,
                    title: `Template ${indexTemplate}`,
                    imageUrlOriginal: singleImage,
                    imageUrlOptimizedSmall: singleImage.replace('original/', '').replace('?accept-experimental', '?width=270&quality=80'),
                    imageUrlOptimizedBig: singleImage.replace('original/', '').replace('?accept-experimental', '?width=1080'),
                }

                templateList.push(singleTemplateItem)
            })

            // TODO: here we got templates info. and we van set images by BRAND (now only AUDI) and with correct image url (now hardcoded)
            setImageTemplateList(templateList);
        }
    }

    const handleCustomerTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCustomerName(event.target.value)
    };
    const handleDealerTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDealerName(event.target.value)
    };

    const handleTextAreaChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCustomerMessage(event.target.value)
    };

    const handleClickImage = (event: React.MouseEvent<HTMLDivElement>) => {
        const templateId = event.currentTarget.getAttribute('value');
        if (!templateId) return;
        setTemplateImageSelected(templateId)
    }

    const formValidation = () => {
        // reset at each iteration
        setFormValidated(false)
        // custmer name error message if too LONG
        customerName.length > CUSTOMER_NAME_LENGTH_MAX ? setCustomerNameError(true) : setCustomerNameError(false);
        // dealer name error message if too LONG
        dealerName.length > DEALER_NAME_LENGTH_MAX ? setDealerNameError(true) : setDealerNameError(false);
        // text message error message if too LONG
        customerMessage.length > CUSTOMER_MESSAGE_LENGTH_MAX ? setCustomerMessageError(true) : setCustomerMessageError(false);
        // real validation
        if (
            customerName.length >= CUSTOMER_NAME_LENGTH_MIN && customerName.length <= CUSTOMER_NAME_LENGTH_MAX
            &&
            // message logic: optional. so can be empty. but if filled, it need to be between min and max length
            (dealerName.length === 0 || (dealerName.length >= DEALER_NAME_LENGTH_MIN && dealerName.length <= DEALER_NAME_LENGTH_MAX))
            && 
            // message logic: optional. so can be empty. but if filled, it need to be between min and max length
            (customerMessage.length === 0 || (customerMessage.length >= CUSTOMER_MESSAGE_LENGTH_MIN && customerMessage.length <= CUSTOMER_MESSAGE_LENGTH_MAX))
            &&            
            templateImageSelected
        ) {
            setFormValidated(true)
        }
    }

    const handleCancel = () => {
        resetFormData();
        setDeviceSelected(null);
        fetchAndSetOptions(dealershipSelected);
    }

    const handleEndActiveWelcomeScreen = async () => {
        setLoading(true);

        if (!deviceSelected?.deviceNumber) {
            // TODO: set a fallback message?
            // Handle error: deviceNumber is required
            setLoading(false);
            return;
        }

        if (deviceSelected.deviceClass === 'AVE'){
            try {
                await dissonnectWelcomeScreenAVE();
            } catch (error) {
                setFeedbackError(true);
            }
        } else {
            await endWelcomeScreenChrombox(deviceSelected.deviceNumber);
        }

        // 1) reset welcome screen logic for next iteration
        resetFormData();
        // 2) no device chosen > show again device list select on Page
        setDeviceSelected(null)

        fetchAndSetOptions(dealershipSelected);

        // 3) remove loader
        setLoading(false);
    }

    const handleFormSubmit = async () => {
        setLoading(true);
        // get current template image url
        let currentTemplateImageUrl = ''
        imageTemplateList.forEach((singleTemplate) => {
            if (singleTemplate.id === Number(templateImageSelected)) {
                // here passed optimized image, "BIG" one
                currentTemplateImageUrl = isBentley ? singleTemplate.imageUrlOriginal : singleTemplate.imageUrlOptimizedBig
            }
        })

        if (!deviceSelected?.deviceNumber) {
            // TODO: set a fallback message?
            // Handle error: deviceNumber is required
            setLoading(false);
            return;
        }

        if (deviceSelected.deviceClass === 'AVE'){
            const customerNameStringGenerated = isBentley
                ? `${customerName}${dealerName && ', ' + t('welcome-screen-welcome-bentley') + ' ' + dealerName + '.'}`
                : `${t('welcome-screen-welcome')}\n${customerName} ${dealerName && t('welcome-screen-to') + ' ' + dealerName}`
            const dealerId = deviceSelected.thingName.split("_")[0];
            const aveId = deviceSelected.thingName.split("_").pop();
            if (aveId && dealerId) {
                try {
                    await connectWelcomeScreenAVE(aveId, dealerId);
                    await setWelcomeScreenAVE('image', currentTemplateImageUrl, customerNameStringGenerated, customerMessage, templateImageSelected === '3' ? { r: 0, g: 0, b: 0, a: 255 } : { r: 245, g: 245, b: 245, a: 255 });
                    setFeedbackSuccess(true);
                 } catch (error) {
                    setFeedbackError(true);
                 }
            }
        } else {
                const customerNameStringGenerated = isBentley
                    ? `${customerName}${dealerName && ', ' + t('welcome-screen-welcome-bentley') + ' ' + dealerName + '.'}`
                    : `${t('welcome-screen-welcome')}\n${customerName} ${dealerName && t('welcome-screen-to') + ' ' + dealerName}`            
                const info = {
                deviceNumber: deviceSelected.deviceNumber,
                url: currentTemplateImageUrl,
                customerName: customerNameStringGenerated,
                message: customerMessage,
                template: templateImageSelected,
            }
            // save data for welcome screen / asset override
            await setWelcomeScreenChrombox(info);
            setFeedbackSuccess(true);
        }

        // 1) reset welcome screen logic for next iteration
        resetFormData();
        // 2) no device chosen > show again device list select on Page
        setDeviceSelected(null);

        fetchAndSetOptions(dealershipSelected);

        // 3) remove loader
        setLoading(false);
    }

    const initForm = async () => {
        //setLoading(true);
        // // Call API get | not so useful now, but for the future
        await getTemplateImageList()
        //setLoading(false);
    }

    // if you got a device selected, show real form
    useEffect(() => {
        if (deviceSelected) {
            initForm()
        }
    }, [deviceSelected]);

    useEffect(() => {
        setCurrentLanguage(i18n.language);
    }, [i18n.language]);

    // if you got a device selected, show real form
    useEffect(() => {
        formValidation()
    }, [
        customerName,
        dealerName,
        customerMessage,
        templateImageSelected,
    ]);

    useEffect(() => {
        setDeviceSelected(null)
        setDealershipSelected(contextDealerId);
        contextDealerId && fetchAndSetOptions(contextDealerId, true);
    }, [contextDealerId]);

    const fetchAndSetOptions = async (id, forceUpdate = false, query = '') => {
        setLoadingDevices(true);
        const devicesResponse = await api.devices.getDevices({
            params: {
                filterKvps: id,
                ...(forceUpdate && { force_update: 1 }),
                ...(query && { 
                    deviceIdentifier: query,
                    title: query
                 }),
            },
        });
        setDevicesList(devicesResponse[RESPONSE.EXPAND_COLLECTION]); 
        setLoadingDevices(false);
    };

    const handleDealerSelection = async (selectedOptions) => { 
        const dealerId = selectedOptions[0].value.split('/').pop();
        fetchAndSetOptions(dealerId, true);
        setDealershipSelected(dealerId);
    }

    const handleQueryChange = async (query: string) => {
        fetchAndSetOptions(dealershipSelected, false, query);
    };

    const debouncedQueryChange = debounce(
        handleQueryChange,
        SEARCH_DEBOUNCE_VALUE
    );

    const renderDealershipSelection = () => {
        return (
            <div className='welcome-screen-step dealership-selection'>
                <p>{t('welcome-screen-description-step-device-select')}</p>
                <ImageStatic
                    classnames='is-welcome-screen-storytelling'
                    imagePath={`./assets/${currentLanguage === 'de' ? 'welcome-screen-storytelling-de' : 'welcome-screen-storytelling-en'}`}
                    altText="storytelling"
                    isImageRwd={true}
                />
                <p>{t('welcome-screen-description-how-to')}</p>
                <p>{t('welcome-screen-description-how-to-01')}</p>
                <p>{t('welcome-screen-description-how-to-02')}</p>
                <p>{t('welcome-screen-description-how-to-03')}</p>
                <p>{t('welcome-screen-description-how-to-04')}</p>
                {isIframeMode ? <h4>{t('welcome-screen-select-dealership')}</h4> : (
                    <DealershipDropdown
                        onChange={handleDealerSelection}
                        defaultOptions={
                            dealerships ? helpers.mapDealershipOptions(dealerships.list) : []
                        }
                    />
                )}
            </div>
        )
    }

    const renderDeviceSelection = () => 
        <div className='welcome-screen-step device-selection'>
            {!isIframeMode && <GoBackButton onClick={() => setDealershipSelected(null)} />}
            <p>{t('welcome-screen-description-step-device-select')}</p>
            <ImageStatic
                classnames='is-welcome-screen-storytelling'
                imagePath={`./assets/${currentLanguage === 'de' ? 'welcome-screen-storytelling-de' : 'welcome-screen-storytelling-en'}`}
                altText="storytelling"
                isImageRwd={true}
            />
            <p>{t('welcome-screen-description-how-to')}</p>
            <p>{t('welcome-screen-description-how-to-01')}</p>
            <p>{t('welcome-screen-description-how-to-02')}</p>
            <p>{t('welcome-screen-description-how-to-03')}</p>
            <p>{t('welcome-screen-description-how-to-04')}</p>
            {loading
                ? <Loader />
                : <MultiSelectDropdown
                    className="dropdown-list-devices"
                    name="dropdown-list-devices"
                    label={`${t('welcome-screen-label-choose-device')} (${dealershipSelected})`}
                    searchPlaceholder={t('welcome-screen-label-filter-device')}
                    isSingleSelect={true}
                    isFilterable={true}
                    options={devicesList.map(singleDevice => ({
                        value: singleDevice.deviceNumber,
                        label: `${singleDevice.title} | ${singleDevice.deviceNumber}`,
                    }))}
                    onQueryChange={debouncedQueryChange}
                    onChange={handleSelectChange}
                    isLoading={loadingDevices}
                />
            }
        </div>

     const renderWelcomeScreenForm = () =>
        <div className='welcome-screen-step welcome-screen-form'>
            <GoBackButton onClick={() => handleCancel()} />
            <h2 className='intro'>{t('welcome-screen-intro-text') + ' ' + deviceSelected?.title + ' | ' + deviceSelected.deviceNumber}
            </h2>
            <InputField
                label={t('welcome-screen-label-customer-name')}
                inputType="input"
                name="welcome-screen-customer-name"
                value={customerName}
                onChangeHandler={handleCustomerTextChange}
                hint={customerName.length === 0
                    ? t('welcome-screen-hint-customer-name')
                    : `${customerName.length}/${CUSTOMER_NAME_LENGTH_MAX}`
                }
                isValidating={true}
                isValid={!isCustomerNameError}
            />
            <InputField
                label={t('welcome-screen-label-dealer-name')}
                inputType="input"
                name="welcome-screen-dealer-name"
                value={dealerName}
                onChangeHandler={handleDealerTextChange}
                hint={dealerName.length === 0
                    ? t('welcome-screen-hint-dealer-name')
                    : `${dealerName.length}/${DEALER_NAME_LENGTH_MAX}`
                }
                isValidating={true}
                isValid={!isDealerNameError}
            />
            <InputField
                label={t('welcome-screen-label-customer-message')}
                inputType="input"
                name="welcome-screen-text-area"
                value={customerMessage}
                onChangeHandler={handleTextAreaChange}
                hint={customerMessage.length === 0
                    ? t('welcome-screen-hint-customer-message')
                    : `${customerMessage.length}/${CUSTOMER_MESSAGE_LENGTH_MAX}`
                }
                isValidating={true}
                isValid={!isCustomerMessageError}
            />
            {imageTemplateList && (
                <div className='welcome-screen-image-container'>
                    {imageTemplateList.map((singleImageTemplate) => ( 
                    <label className="radio-card" key={singleImageTemplate.id}>
                        <input
                            value={singleImageTemplate.id}
                            name="welcome-screen-image-template"
                            onClick={handleClickImage}
                            type='radio'
                            onChange={() => {}}
                            style={{ accentColor: 'black' }}
                        />
                        <ImageStatic
                            classnames='welcome-screen-single-image-wrapper'
                            imagePath={isBentley ? singleImageTemplate.imageUrlOriginal : singleImageTemplate.imageUrlOptimizedSmall}
                            altText={singleImageTemplate.title}
                            isImageRwd={true}
                            isSimpleImage={true}
                        />
                    </label>
                    ))}
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    text={t('welcome-screen-submit')}
                    extraClass="submit-button"
                    clickHandler={handleFormSubmit}
                    disabled={!isFormValidated}
                    isLoading={loading}
                />
                <Button
                    text={`${t('welcome-screen-cancel')}*`}
                    clickHandler={handleEndActiveWelcomeScreen}
                    extraClass="end-button"
                    isLoading={loading}
                />
                <Button
                    text={t('cancel')}
                    clickHandler={handleCancel}
                    extraClass="cancel-button"
                />
            </div>
            <span className='cancel-hint'>* {t('welcome-screen-cancel-hint')}</span>
        </div>

    const renderSuccessModal = () =>
        <Modal
            show
            onClose={closeModalSuccess}
        >
            <Modal.Title>
                {t('welcome-screen-feedback-success-heading')}
            </Modal.Title>
            <Modal.Content>
                <p>{t('welcome-screen-feedback-success-description')}</p>
                <Button
                    text={t('welcome-screen-feedback-success-btn')}
                    clickHandler={closeModalSuccess}
                />
            </Modal.Content>
        </Modal>

    const renderErrorModal = () =>
        <Modal
            show
            onClose={closeModalError}
        >
            <Modal.Title>
                {t('welcome-screen-feedback-error-heading')}
            </Modal.Title>
            <Modal.Content>
                <p>{t('welcome-screen-feedback-error-connection')}</p>
                <Button
                    text={t('welcome-screen-feedback-error-btn')}
                    clickHandler={closeModalError}
                />
            </Modal.Content>
        </Modal>

    return (
        <div className={isBentley ? 'b-welcome-screen-view-bentley' : 'b-welcome-screen-view'}>
            {!deviceSelected && !dealershipSelected && renderDealershipSelection()}
            {!deviceSelected && dealershipSelected && renderDeviceSelection()}
            {deviceSelected && dealershipSelected && renderWelcomeScreenForm()}
            {isFeedbackSuccess && renderSuccessModal()}
            {isFeedbackError && renderErrorModal()}
        </div>
    );
};

export default WelcomeScreenView 